import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlockingMutation } from '../hooks/useBlockingMutation';
import { Window, Button, TextField, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { devicesApi, ApiClientError } from '../api/client';
import { useAdminStore } from '../stores/adminStore';
import { formatTimestamp } from '../utils/formatTimestamp';
import styles from './DeviceExclusions.module.css';

export function DeviceExclusions() {
  const queryClient = useQueryClient();
  const { hasRole } = useAdminStore();

  const [page, setPage] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [visitorId, setVisitorId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['device-exclusions', { limit, offset: page * limit }],
    queryFn: () => devicesApi.listExclusions({ limit, offset: page * limit }),
  });

  const addMutation = useBlockingMutation({
    loadingMessage: 'Adding exclusion...',
    mutationFn: (data: { visitor_id: string; reason: string }) =>
      devicesApi.addExclusion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-exclusions'] });
      setShowAddForm(false);
      setVisitorId('');
      setReason('');
      setError('');
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to add exclusion');
    },
  });

  const removeMutation = useBlockingMutation({
    loadingMessage: 'Removing exclusion...',
    mutationFn: (id: string) => devicesApi.removeExclusion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-exclusions'] });
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to remove exclusion');
    },
  });

  const exclusions = data?.exclusions || [];
  const total = data?.pagination?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!visitorId.trim()) {
      setError('Visitor ID is required');
      return;
    }
    if (!reason.trim() || reason.trim().length < 3) {
      setError('Reason must be at least 3 characters');
      return;
    }
    addMutation.mutate({ visitor_id: visitorId.trim(), reason: reason.trim() });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Device Exclusions</h1>
        {hasRole('super') && (
          <Button onClick={() => setShowAddForm(true)}>
            Add Exclusion
          </Button>
        )}
      </div>

      <p className={styles.description}>
        Excluded devices bypass device-account enforcement checks.
        Use for developer devices and shared testing environments.
      </p>

      {isLoading ? (
        <div className={styles.loading}>Loading...</div>
      ) : exclusions.length > 0 ? (
        <>
          <div className={styles.tableWrapper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Visitor ID</TableHeader>
                  <TableHeader>Reason</TableHeader>
                  <TableHeader>Created By</TableHeader>
                  <TableHeader>Created At</TableHeader>
                  {hasRole('super') && <TableHeader>Actions</TableHeader>}
                </TableRow>
              </TableHead>
              <TableBody>
                {exclusions.map((exclusion) => (
                  <TableRow key={exclusion.id}>
                    <TableCell>
                      <span className={styles.visitorId} title={exclusion.visitor_id}>
                        {exclusion.visitor_id.substring(0, 16)}...
                      </span>
                    </TableCell>
                    <TableCell>{exclusion.reason}</TableCell>
                    <TableCell>{exclusion.created_by}</TableCell>
                    <TableCell>
                      <span className={styles.timestamp}>
                        {formatTimestamp(exclusion.created_at)}
                      </span>
                    </TableCell>
                    {hasRole('super') && (
                      <TableCell>
                        <Button
                          onClick={() => removeMutation.mutate(exclusion.id)}
                          loading={removeMutation.isPending}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles.empty}>No device exclusions configured</div>
      )}

      {showAddForm && (
        <div className={styles.modalOverlay} onClick={() => setShowAddForm(false)}>
          <Window title="Add Device Exclusion" maxWidth={500} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <form onSubmit={handleAddSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}

              <TextField
                label="Visitor ID"
                value={visitorId}
                onChange={(e) => setVisitorId(e.target.value)}
                required
              />

              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              />

              <div className={styles.formActions}>
                <Button type="button" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={addMutation.isPending}>
                  Add Exclusion
                </Button>
              </div>
            </form>
          </Window>
        </div>
      )}
    </div>
  );
}
